import React, { useRef, useState } from "react";
import { View, TouchableOpacity, StyleSheet, Platform } from "react-native";
import { PALETTE } from "../lib/palette";
import { Chevron } from "./Chevron";

const BOTTOM_THRESHOLD = 24;   // 바닥에서 이 이상 남았을 때만 "더 있음" 힌트 표시(무깜빡)

// 데스크탑(마우스=fine pointer)에서는 세로 스크롤바를 노출해 드래그 스크롤을 지원한다.
// 태블릿/폰(터치=coarse pointer)에서는 종전대로 숨겨(셰브론+페이드가 어포던스 대체) 중앙정렬을 유지한다.
const SHOW_VERTICAL_SCROLLBAR =
  Platform.OS === "web" &&
  typeof window !== "undefined" &&
  typeof window.matchMedia === "function" &&
  window.matchMedia("(pointer: fine)").matches;

/**
 * ScrollView/FlatList에 붙여 "아래에 더 있음"을 판정하는 훅.
 * scrollProps를 컨테이너에 스프레드하고, showHint로 ScrollMoreHint를 토글, scrollToEnd로 탭 스크롤.
 * ScrollView·FlatList 모두 동일 핸들러/ref(scrollToEnd)를 지원해 그대로 재사용된다.
 */
export function useScrollMore() {
  const ref = useRef<any>(null);
  const view = useRef(0);       // 보이는 높이
  const content = useRef(0);    // 전체 내용 높이
  const offset = useRef(0);     // 현재 스크롤 위치
  const [showHint, setShowHint] = useState(false);

  const recompute = () => {
    const scrollable = content.current > view.current + 4;
    const moreBelow = content.current - offset.current - view.current > BOTTOM_THRESHOLD;
    setShowHint(scrollable && moreBelow);
  };
  const onLayout = (e: any) => { view.current = e.nativeEvent.layout.height; recompute(); };
  const onContentSizeChange = (_w: number, h: number) => { content.current = h; recompute(); };
  const onScroll = (e: any) => {
    const n = e.nativeEvent;
    offset.current = n.contentOffset.y;
    view.current = n.layoutMeasurement.height;
    content.current = n.contentSize.height;
    recompute();
  };
  // FlatList는 웹에서 scrollToEnd가 불안정 → 측정된 콘텐츠 높이로 scrollToOffset(끝으로 clamp).
  // ScrollView는 scrollToEnd 사용. (FlatList엔 scrollToOffset, ScrollView엔 scrollTo/scrollToEnd만 존재)
  const scrollToEnd = () => {
    const r: any = ref.current;
    if (!r) return;
    try {
      if (typeof r.scrollToOffset === "function") {
        r.scrollToOffset({ offset: Math.max(0, content.current), animated: true });
      } else if (typeof r.scrollToEnd === "function") {
        r.scrollToEnd({ animated: true });
      } else if (typeof r.scrollTo === "function") {
        r.scrollTo({ y: Math.max(0, content.current), animated: true });
      }
    } catch (_) {}
  };

  return {
    showHint,
    scrollToEnd,
    // 터치(태블릿/폰): 웹 스크롤바를 숨겨(chevron+페이드가 어포던스 대체) 오버레이가 스크롤바 폭만큼
    // 오른쪽으로 치우쳐 보이는 문제를 없앤다. 데스크탑(마우스): 스크롤바를 노출해 드래그 스크롤을 지원한다.
    scrollProps: { ref, onLayout, onContentSizeChange, onScroll, scrollEventThrottle: 16, showsVerticalScrollIndicator: SHOW_VERTICAL_SCROLLBAR },
  };
}

/**
 * 스크롤 영역 하단 중앙 "더 있음" 힌트: 하단 페이드(흰색 밴드 스택으로 그라데이션 에뮬레이트) + ↓ 셰브론 버튼.
 * 부모는 position:relative(스크롤 컨테이너를 감싼 View)여야 하며, 이 오버레이는 그 하단에 절대배치된다.
 * pointerEvents=box-none으로 셰브론만 탭 가능하고 나머지는 스크롤로 통과된다.
 */
export function ScrollMoreHint({ visible, onPress }: { visible: boolean; onPress: () => void }) {
  if (!visible) return null;
  const bands = [0.0, 0.12, 0.28, 0.5, 0.72, 0.92];   // 위=투명 → 아래=거의 흰색
  return (
    <View pointerEvents="box-none" style={styles.wrap}>
      <View pointerEvents="none" style={styles.fade}>
        {bands.map((a, i) => (
          <View key={i} style={{ flex: 1, backgroundColor: `rgba(255,255,255,${a})` }} />
        ))}
      </View>
      <TouchableOpacity onPress={onPress} activeOpacity={0.7} style={styles.chevron}>
        <Chevron direction="down" size={9} thickness={2} color={PALETTE.MUTED} />
      </TouchableOpacity>
    </View>
  );
}

const styles = StyleSheet.create({
  wrap: {
    position: "absolute", left: 0, right: 0, bottom: 0, height: 60,
    alignItems: "center", justifyContent: "flex-end",
  },
  fade: {
    position: "absolute", left: 0, right: 0, bottom: 0, height: 44,
    flexDirection: "column",
  },
  chevron: {
    width: 34, height: 34, borderRadius: 17,
    backgroundColor: PALETTE.SURFACE,
    borderWidth: 1, borderColor: PALETTE.BORDER,
    alignItems: "center", justifyContent: "center",
    marginBottom: 8,
    shadowColor: "#000", shadowOpacity: 0.12, shadowRadius: 4,
    shadowOffset: { width: 0, height: 2 }, elevation: 3,
  },
});
